import * as React from "react";
import { Loader2, Printer } from "lucide-react";
import { toast } from "sonner";

import { Button } from "@workspace/ui/components/button";

import { useEposContext } from "../epos-context";
import { printReceiptFailure } from "./print-receipt-error";

/**
 * HA-360 Part B — prints the tab's bill or receipt to this terminal's receipt printer. The server decides which (an open
 * tab's bill, a settled one's receipt) and says, so the toast reflects what printed. A refusal says why, with Retry where
 * trying again can help, as an order's print does (HA-361); the button disables while in flight (the server's 2s debounce
 * is authoritative).
 */
export function PrintTabButton({
	tabId,
	label,
	variant = "outline",
	className,
}: {
	tabId: string;
	label: string;
	variant?: "default" | "secondary" | "outline";
	className?: string;
}) {
	const { printTab } = useEposContext();
	const [isPrinting, setIsPrinting] = React.useState(false);

	const handlePrint = async () => {
		setIsPrinting(true);
		try {
			const result = await printTab(tabId);
			if (!result.absorbed) {
				toast.success(
					result.documentType === "TabReceipt"
						? "Tab receipt sent to printer"
						: "Tab bill sent to printer",
				);
			}
		} catch (error) {
			// Nothing was dispatched or recorded, so Retry just asks again.
			const failure = printReceiptFailure(error);
			toast.error(failure.message, {
				action: failure.retryable
					? { label: "Retry", onClick: () => void handlePrint() }
					: undefined,
			});
		} finally {
			setIsPrinting(false);
		}
	};

	return (
		<Button
			variant={variant}
			className={className}
			disabled={isPrinting}
			onClick={handlePrint}
		>
			{isPrinting ? (
				<Loader2 className="size-4 animate-spin" />
			) : (
				<Printer className="size-4" />
			)}
			{label}
		</Button>
	);
}
